import React, { useEffect, useState } from 'react';
import { useDragToBoardStore } from '@/stores/dragToBoardStore';
import { useCanvasStore } from '@/stores/canvasStore';

export function DragGhostPreview() {
  const { isDragging, draggedItemIds, hoverBoardId } = useDragToBoardStore();
  const { items } = useCanvasStore();
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      setPosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isDragging]);

  // Only show ghost preview when hovering over a board target, not during regular drags
  if (!isDragging || draggedItemIds.length === 0 || !hoverBoardId) return null;

  const draggedItems = items.filter(i => draggedItemIds.includes(i.id));
  const displayItems = draggedItems.slice(0, 3);
  const count = draggedItemIds.length;

  // Get preview text for an item
  const getItemPreview = (item: typeof items[0]) => {
    const content = item.content as Record<string, unknown>;
    switch (item.item_type) {
      case 'sticky':
        return (content.text as string)?.slice(0, 30) || 'Note';
      case 'text_card':
        return (content.body as string)?.slice(0, 30) || 'Text';
      case 'document':
        return (content.title as string)?.slice(0, 30) || 'Document';
      case 'image':
        return (content.alt as string)?.slice(0, 30) || 'Image';
      case 'file':
        return (content.filename as string)?.slice(0, 30) || 'File';
      case 'link':
        return (content.title as string)?.slice(0, 30) || 'Link';
      case 'video':
        return (content.filename as string)?.slice(0, 30) || 'Video';
      default:
        return 'Item';
    }
  };

  // Get color for item type
  const getItemColor = (item: typeof items[0]) => {
    const content = item.content as Record<string, unknown>;
    if (item.item_type === 'sticky') {
      const color = content.color as string;
      const colors: Record<string, string> = {
        yellow: 'bg-sticky-yellow',
        blue: 'bg-sticky-blue',
        pink: 'bg-sticky-pink',
        green: 'bg-sticky-green',
      };
      return colors[color] || 'bg-sticky-yellow';
    }
    return 'bg-card';
  };

  return (
    <div
      className="fixed pointer-events-none z-[9999]"
      style={{
        left: position.x + 16,
        top: position.y + 16,
        opacity: hoverBoardId ? 0.9 : 0.75,
        transform: `scale(${hoverBoardId ? 1.05 : 1})`,
        transition: 'opacity 150ms, transform 150ms',
      }}
    >
      {/* Stack of cards */}
      <div className="relative">
        {displayItems.map((item, index) => (
          <div
            key={item.id}
            className={`absolute rounded-lg shadow-lg border border-border px-3 py-2 text-sm ${getItemColor(item)}`}
            style={{
              transform: `translateY(${index * -4}px) translateX(${index * 4}px) rotate(${index * 2 - 2}deg)`,
              zIndex: 3 - index,
              minWidth: '120px',
              maxWidth: '180px',
            }}
          >
            <span className="text-foreground truncate block">{getItemPreview(item)}</span>
          </div>
        ))}

        {/* Count badge */}
        {count > 1 && (
          <div 
            className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-lg"
            style={{ zIndex: 10 }}
          >
            {count}
          </div>
        )}
      </div>
    </div>
  );
}
